import { useState } from 'react';
import { api } from '../lib/api.js';
import { useApi, useAction } from '../lib/hooks.js';
import { Loading, ErrorNote, Empty, Modal, formatQty, typedQty, announce } from '../components/ui.jsx';
import ItemPicker from '../components/ItemPicker.jsx';

/**
 * Split a merged row's notes into the amounts it absorbed and everything else.
 *
 * The server writes one segment per thing it could not fold into the row's
 * number -- "also: 2 sprig" when a sprig will not become a teaspoon -- joined
 * with "; ". The two kinds are rendered differently because they are worth
 * different things to somebody standing in a shop: an amount is a quantity to
 * buy and must survive at any width, and the rest is context.
 */
function readNotes(item) {
  const segments = String(item.notes || '').split('; ').map((s) => s.trim()).filter(Boolean);
  return {
    amounts: segments.filter((s) => s.startsWith('also: ')).map((s) => s.slice('also: '.length)),
    aside: segments.filter((s) => !s.startsWith('also: ')),
  };
}

/** Req #10: the shopping list, grouped by aisle. */
export default function ShoppingList() {
  const list = useApi('/shopping-lists/active');
  // Empty until an affiliate application is approved, which is the normal
  // state rather than a failure -- so this renders nothing at all rather than
  // an empty section explaining itself.
  const referrals = useApi('/referrals');
  const [adding, setAdding] = useState(false);
  const [copied, setCopied] = useState(false);
  const [added, setAdded] = useState(null);
  const { busy, error, run } = useAction();

  if (list.loading && !list.data) return <Loading rows={5} />;
  if (list.error) return <ErrorNote error={list.error} onRetry={list.reload} />;
  if (!list.data) return null;

  const data = list.data;

  // M2: a line linked to a pantry item becomes stock the moment it is ticked.
  // Said out loud but briefly -- it is a confirmation, not news, and the person
  // reading it is standing in a shop.
  const toggle = (item) => run(async () => {
    const result = await api.patch(`/shopping-lists/${data.id}/items/${item.id}`, { purchased: !item.purchased });
    if (result?.inventory_added) {
      announce(`${result.inventory_added.name} added to the pantry`);
      setAdded(result.inventory_added.name);
      setTimeout(() => setAdded(null), 2500);
    }
    list.reload();
  });

  const removeItem = (item) => run(async () => {
    await api.delete(`/shopping-lists/${data.id}/items/${item.id}`);
    list.reload();
  });

  const clearPurchased = () => run(async () => {
    await api.post(`/shopping-lists/${data.id}/clear-purchased`, {});
    list.reload();
  });

  const addLowStock = () => run(async () => {
    await api.post(`/shopping-lists/${data.id}/from-low-stock`, {});
    list.reload();
  });

  // Through run(), so a failure lands in the ErrorNote like every other action
  // here -- an async click handler that rejects on its own changes nothing on
  // screen. Success says so on the button: the clipboard gives no sign at all.
  const copy = () => run(async () => {
    const text = await api.get(`/shopping-lists/${data.id}/text`);
    if (!navigator.clipboard) {
      throw new Error('This browser does not let the page write to the clipboard.');
    }
    await navigator.clipboard.writeText(typeof text === 'string' ? text : JSON.stringify(text));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  });

  const purchasedCount = data.items.length - data.remaining;

  return (
    <div className="stack">
      <ErrorNote error={error} />
      {added && <div className="alert info" role="status">Added to pantry: {added}</div>}

      <div className="row-between">
        <div>
          <h2>{data.name}</h2>
          <div className="small muted">
            {data.remaining} to buy
            {purchasedCount > 0 && ` · ${purchasedCount} in the cart`}
          </div>
        </div>
        <button type="button" className="primary" onClick={() => setAdding(true)}>+ Add</button>
      </div>

      {data.items.length === 0 && (
        <Empty title="Nothing on the list">
          Add items by hand, pull in what a recipe needs, or restock your staples.
        </Empty>
      )}

      {data.groups.map((group) => (
        <section key={group.category}>
          <div className="section-title">
            <h2 style={{ textTransform: 'capitalize' }}>{group.category}</h2>
            <span className="count">{group.items.filter((i) => !i.purchased).length}</span>
          </div>
          <div className="card list">
            {group.items.map((item) => {
              const { amounts, aside } = readNotes(item);
              return (
                <div key={item.id} className="list-item">
                  <label className="checkbox" style={{ flex: 1, minWidth: 0 }}>
                    <input
                      type="checkbox"
                      checked={Boolean(item.purchased)}
                      onChange={() => toggle(item)}
                      disabled={busy}
                    />
                    <span className="grow" style={{ minWidth: 0 }}>
                      <span className={`truncate ${item.purchased ? 'strike' : ''}`} style={{ display: 'block' }}>
                        {formatQty(item.quantity, item.unit)} {item.name}
                      </span>
                      {(item.recipe_title || amounts.length > 0 || aside.length > 0) && (
                        <span className="tiny muted list-meta">
                          {/* The second amount leads, and does not shrink. It is
                              the reason the two lines became one: a merged row
                              that cannot show it is worse than the duplicate it
                              replaced. What gives way instead is the prose after
                              it, which the reader can do without. */}
                          {amounts.map((amount) => (
                            <span key={amount} className="amount">+ {amount}</span>
                          ))}
                          {(item.recipe_title || aside.length > 0) && (
                            <span className="truncate">
                              {[item.recipe_title && `for ${item.recipe_title}`, ...aside]
                                .filter(Boolean).join(' · ')}
                            </span>
                          )}
                        </span>
                      )}
                    </span>
                  </label>
                  <button
                    type="button"
                    className="icon ghost"
                    aria-label={`Remove ${item.name}`}
                    onClick={() => removeItem(item)}
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      ))}

      {data.items.length > 0 && (
        /* Said once, at the bottom, rather than on every unlinked line: the
           list is read while walking a shop, and a caveat repeated per row is
           noise. A line with no pantry item is not added to stock, and this is
           the only place that says so. */
        <p className="tiny muted" style={{ margin: 0 }}>
          Items linked to a pantry item are added when checked off.
        </p>
      )}

      <hr />

      <div className="row wrap" style={{ gap: 10 }}>
        <button type="button" className="grow" disabled={busy} onClick={addLowStock}>Restock staples</button>
        {purchasedCount > 0 && (
          <button type="button" className="grow" disabled={busy} onClick={clearPurchased}>
            Clear {purchasedCount} bought
          </button>
        )}
        <button type="button" className="grow" disabled={busy} onClick={copy}>
          {copied ? 'Copied ✓' : 'Copy as text'}
        </button>
      </div>

      <OrderFrom partners={referrals.data?.partners ?? []} listId={data.id} />

      {adding && (
        <AddItem
          listId={data.id}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); list.reload(); }}
        />
      )}
    </div>
  );
}

/**
 * Ordering the list somewhere, which is how this app earns.
 *
 * Here rather than anywhere else because this is the one screen already
 * holding a list of things somebody is about to buy, so the offer is useful
 * rather than an advertisement wearing a hat.
 *
 * Two modes, and the line between them is consent. `Open` is a plain affiliate
 * link that carries a tag and nothing else, so it needs no permission. `Send
 * list` transmits item names and quantities, which is a disclosure -- so the
 * first time it is used, it asks.
 */
function OrderFrom({ partners, listId }) {
  const [asking, setAsking] = useState(null);
  const [terms, setTerms] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(null);

  if (!partners.length) return null;

  // Reported, not awaited. A window opened after an await is a popup blocker's
  // definition of a popup, and losing the click is worse than losing the count
  // of it -- so the link navigates on its own and this just records.
  const recordLink = (partner) => {
    api.post('/referrals/click', { partner: partner.id, placement: 'shopping_list' })
      .catch(() => {});
  };

  // The cart handoff has to await the server, because the server decides what
  // leaves. So the window is opened first and pointed afterwards: opening it in
  // the click keeps it a user gesture, which is what a blocker looks for.
  const sendList = async (partner) => {
    setFailed(null);
    setBusy(true);
    // window.open returns null whenever the features string asks for noopener,
    // in every browser -- which made this always fall through to navigating the
    // app itself away. So the isolation is applied by hand: open plainly, still
    // inside the click gesture a popup blocker looks for, then sever the
    // back-reference before the window is pointed anywhere.
    const opened = window.open('', '_blank');
    if (opened) opened.opener = null;
    try {
      const result = await api.post('/referrals/cart', { partner: partner.id, list_id: listId });
      if (opened) opened.location = result.url;
      else window.location.assign(result.url);
    } catch (error) {
      if (opened) opened.close();
      // Permission has not been given yet, and the server sent the wording to
      // ask with. Raise it here rather than sending them to Settings to find it.
      if (error.code === 'CART_CONSENT_REQUIRED') {
        setTerms(error.data?.terms ?? null);
        setAsking(partner);
      } else {
        setFailed(error.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const agree = async () => {
    setBusy(true);
    try {
      await api.post('/referrals/cart-sharing', { enabled: true });
      const partner = asking;
      setAsking(null);
      await sendList(partner);
    } catch (error) {
      setFailed(error.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 12 }}>
      <span className="small muted">Order these from</span>
      {failed && <ErrorNote error={{ message: failed }} />}
      <div className="row wrap" style={{ gap: 10, marginTop: 6 }}>
        {partners.map((partner) => (
          <span key={partner.id} className="row" style={{ gap: 6 }}>
            <a
              className="button"
              href={partner.url}
              target="_blank"
              // noreferrer as well as noopener: the referrer would name the page
              // they came from, and this one is a shopping list.
              rel="noopener noreferrer nofollow sponsored"
              onClick={() => recordLink(partner)}
            >
              {partner.label}
            </a>
            {partner.cart && (
              <button type="button" disabled={busy} onClick={() => sendList(partner)}>
                Send list
              </button>
            )}
          </span>
        ))}
      </div>

      {asking && terms && (
        <Modal
          title={terms.title}
          onClose={() => setAsking(null)}
          footer={(
            <>
              <button type="button" onClick={() => setAsking(null)}>Not now</button>
              <button type="button" className="primary" disabled={busy} onClick={agree}>
                Send my list to {asking.label}
              </button>
            </>
          )}
        >
          {/* The four things worth being specific about: what goes, what does
              not, who holds it after, and how to stop. Rendered from the
              server's wording so the consent is recorded against the same
              words that were shown. */}
          <p><strong>What we send:</strong> {terms.shares}</p>
          <p><strong>What we do not:</strong> {terms.withholds}</p>
          <p>{terms.handling}</p>
          <p className="small muted">{terms.revocable}</p>
        </Modal>
      )}
    </div>
  );
}
function AddItem({ listId, onClose, onSaved }) {
  const [item, setItem] = useState(null);
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [unit, setUnit] = useState('');
  const { busy, error, run } = useAction();

  const save = () => run(async () => {
    await api.post(`/shopping-lists/${listId}/items`, {
      master_item_id: item?.id,
      name: item?.name || name.trim(),
      // Blank falls to the server's own default of 1 rather than Number('')'s 0.
      quantity: typedQty(quantity),
      unit: unit || item?.default_unit || 'each',
    });
    onSaved();
  });

  const canSave = Boolean(item || name.trim());

  return (
    <Modal title="Add to list" onClose={onClose}>
      <div className="stack">
        <ErrorNote error={error} />

        <label>
          <span className="label-text">Something you&rsquo;ve bought before</span>
          <ItemPicker value={item} onChange={setItem} allowCreate={false} autoFocus />
        </label>

        {!item && (
          <label>
            <span className="label-text">…or just type it</span>
            <input value={name} onChange={(event) => setName(event.target.value)} placeholder="Fresh dill" />
          </label>
        )}

        <div className="field-row">
          <label>
            <span className="label-text">Quantity</span>
            <input type="number" inputMode="decimal" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          </label>
          <label>
            <span className="label-text">Unit</span>
            <input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder="each" />
          </label>
        </div>

        <button type="button" className="primary full" disabled={busy || !canSave} onClick={save}>
          {busy ? <><span className="spinner" /> Adding</> : 'Add'}
        </button>
      </div>
    </Modal>
  );
}
